import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { AuditLog } from '@bliks-io/contracts';
import {
  DataTable,
  Page,
  PageContent,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  type DataTableColumnDef
} from '@bliks-io/ui';
import {
  useListSuperAdminAuditLogs,
  type AuditLogFilters
} from '@/pages/admin/api/queries/useSuperAdminAuditLogs';
import { useListTenants } from '@/pages/admin/api/queries/useTenants';
import {
  ACTION_LABEL_KEYS,
  DEFAULT_PAGE_SIZE,
  SUPERADMIN_ENTITY_TYPES,
  TENANT_ENTITY_TYPES,
  getActionLabel,
  getEntityTypeLabel,
  parseDateRange
} from './auditLogs.constants';
import { ChangesDetail } from './ChangesDetail';
import { AuditLogDetailDialog } from './AuditLogDetailDialog';
import { createAuditLogColumns } from './auditLogColumns';

export function AuditLogsListPage() {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const search = useSearch({ from: '/admin/audit-logs/' });

  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const openLogDetail = useCallback((log: AuditLog) => {
    setSelectedLog(log);
    setDetailDialogOpen(true);
  }, []);

  const filters = useMemo<AuditLogFilters>(() => {
    const dateRange = parseDateRange(search.auditLogs_timestamp_filter);
    return {
      tenantId: search.tenantId,
      entityType: search.auditLogs_entityType_filter,
      entityId: search.auditLogs_entityId_filter,
      actorId: search.auditLogs_actorId_filter,
      action: search.auditLogs_action_filter,
      dateFrom: dateRange.from,
      dateTo: dateRange.to,
      page: search.page ?? 1,
      limit: search.limit ?? DEFAULT_PAGE_SIZE
    };
  }, [search]);

  const { data: tenantsData } = useListTenants();
  const { data, isLoading, isPlaceholderData } = useListSuperAdminAuditLogs(filters);

  const entityTypeOptions = useMemo(
    () =>
      (search.tenantId
        ? TENANT_ENTITY_TYPES
        : [...SUPERADMIN_ENTITY_TYPES, ...TENANT_ENTITY_TYPES]
      ).map((type) => ({ label: getEntityTypeLabel(t, type), value: type })),
    [search.tenantId, t]
  );

  const actionOptions = useMemo(
    () =>
      Object.keys(ACTION_LABEL_KEYS).map((key) => ({
        label: getActionLabel(t, key),
        value: key
      })),
    [t]
  );

  const setColumnFilter = useCallback(
    (columnId: string, value: string) => {
      navigate({
        to: '/admin/audit-logs',
        search: { ...search, [`auditLogs_${columnId}_filter`]: value, page: undefined }
      });
    },
    [navigate, search]
  );

  const goToPage = (page: number) => {
    navigate({
      to: '/admin/audit-logs',
      search: { ...search, page: page > 1 ? page : undefined }
    });
  };

  const columns = useMemo<DataTableColumnDef<AuditLog>[]>(
    () =>
      createAuditLogColumns({
        t,
        actors: data?.actors,
        actionOptions,
        entityTypeOptions,
        setColumnFilter,
        onSelect: openLogDetail
      }),
    [t, data?.actors, actionOptions, entityTypeOptions, setColumnFilter, openLogDetail]
  );

  return (
    <Page>
      <PageContent>
        <div className="mb-6">
          <h1 className="text-2xl font-bold">{t('auditLogs.title')}</h1>
          <p className="text-muted-foreground">{t('auditLogs.description')}</p>
        </div>

        {/* Primary toolbar: tenant scope selector */}
        <div className="mb-4">
          <Label>{t('auditLogs.scope')}</Label>
          <Select
            value={search.tenantId ?? ''}
            onValueChange={(v) =>
              navigate({
                to: '/admin/audit-logs',
                search: { ...search, tenantId: v || undefined, page: undefined }
              })
            }
          >
            <SelectTrigger
              allowClear
              hasValue={!!search.tenantId}
              onClear={() =>
                navigate({
                  to: '/admin/audit-logs',
                  search: { ...search, tenantId: undefined, page: undefined }
                })
              }
            >
              <SelectValue placeholder={t('auditLogs.superadminScope')} />
            </SelectTrigger>
            <SelectContent>
              {tenantsData?.tenants?.map((tenant) => (
                <SelectItem key={tenant._id} value={tenant._id}>
                  {tenant.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* A skeleton here would unmount the column-filter input the admin is typing in. */}
        <div
          className={isPlaceholderData ? 'opacity-50' : undefined}
          aria-busy={isPlaceholderData || undefined}
        >
          <DataTable
            columns={columns}
            data={data?.logs ?? []}
            isLoading={isLoading}
            enableExpanding
            enableSorting
            enableColumnFilters
            enableUrlState
            tableId="auditLogs"
            enablePagination={false}
            getRowCanExpand={(row) =>
              !!(row.original.changes?.length || row.original.batchInfo || row.original.snapshot)
            }
            renderSubComponent={({ row }) => <ChangesDetail log={row.original} />}
            emptyMessage={t('auditLogs.empty')}
            initialPageSize={DEFAULT_PAGE_SIZE}
          />
        </div>

        {data && data.total > data.limit && (
          <div className="flex items-center justify-between mt-4">
            <span className="text-sm text-muted-foreground">
              {t('auditLogs.showing', {
                from: (data.page - 1) * data.limit + 1,
                to: Math.min(data.page * data.limit, data.total),
                total: data.total
              })}
            </span>
            <div className="flex gap-2">
              <button
                className="px-3 py-1 border rounded text-sm disabled:opacity-50"
                disabled={data.page <= 1 || isPlaceholderData}
                onClick={() => goToPage(data.page - 1)}
              >
                {t('auditLogs.previous')}
              </button>
              <button
                className="px-3 py-1 border rounded text-sm disabled:opacity-50"
                disabled={data.page * data.limit >= data.total || isPlaceholderData}
                onClick={() => goToPage(data.page + 1)}
              >
                {t('auditLogs.next')}
              </button>
            </div>
          </div>
        )}
        {selectedLog && (
          <AuditLogDetailDialog
            log={selectedLog}
            actors={data?.actors}
            open={detailDialogOpen}
            onOpenChange={setDetailDialogOpen}
          />
        )}
      </PageContent>
    </Page>
  );
}
